export const RADAR_DIMENSION_KEYS = [
  'technical_skills',
  'experience_level',
  'domain_expertise',
  'problem_solving',
  'tech_stack',
  'transferable_skills',
  'education_certs',
  'collaboration_leadership',
] as const;

export type RadarDimensionKey = typeof RADAR_DIMENSION_KEYS[number];

type RadarDimensionLabelMeta = {
  shortLabel: string;
  fullLabel: string;
};

type RadarDimensionTooltipMeta = {
  summary: string;
  onetMapping: string;
  shrmMapping: string;
};

export const RADAR_DIMENSION_LABELS: Record<RadarDimensionKey, RadarDimensionLabelMeta> = {
  technical_skills: { shortLabel: 'Technical', fullLabel: 'Technical Skills' },
  experience_level: { shortLabel: 'Experience', fullLabel: 'Experience & Scope' },
  domain_expertise: { shortLabel: 'Knowledge', fullLabel: 'Occupational Knowledge' },
  problem_solving: { shortLabel: 'Cognitive', fullLabel: 'Cognitive Abilities' },
  tech_stack: { shortLabel: 'Tools', fullLabel: 'Tools & Technology' },
  transferable_skills: { shortLabel: 'Transferable', fullLabel: 'Transferable Competencies' },
  education_certs: { shortLabel: 'Credentials', fullLabel: 'Credentials & Learning' },
  collaboration_leadership: { shortLabel: 'Work Style', fullLabel: 'Work Style, Collaboration & Leadership' },
};

export const RADAR_DIMENSION_TOOLTIPS: Record<RadarDimensionKey, RadarDimensionTooltipMeta> = {
  technical_skills: {
    summary: 'Role-specific technical capability evidenced through delivered work outcomes.',
    onetMapping: 'Skills (essential and transferable)',
    shrmMapping: 'Functional capability and execution quality',
  },
  experience_level: {
    summary: 'Scope, responsibility level, and complexity of work handled.',
    onetMapping: 'Experience requirements and occupational requirements',
    shrmMapping: 'Role level, accountability, and operating scope',
  },
  domain_expertise: {
    summary: 'Industry and occupational knowledge relevant to the target role context.',
    onetMapping: 'Knowledge and work context',
    shrmMapping: 'Business context fluency and domain understanding',
  },
  problem_solving: {
    summary: 'Reasoning quality, troubleshooting rigor, and architecture judgment.',
    onetMapping: 'Abilities (reasoning and analysis)',
    shrmMapping: 'Judgment and analytical decision quality',
  },
  tech_stack: {
    summary: 'Depth and recency of tools, platforms, and ecosystem fit.',
    onetMapping: 'Tools and technology requirements',
    shrmMapping: 'Platform readiness and technical environment fit',
  },
  transferable_skills: {
    summary: 'Portable competencies that transfer across functions and contexts.',
    onetMapping: 'Generalized work activities and transferable skills',
    shrmMapping: 'Adaptability and cross-functional mobility',
  },
  education_certs: {
    summary: 'Role-relevant credentials and continuous learning signals.',
    onetMapping: 'Education and credentials',
    shrmMapping: 'Qualification evidence and development rigor',
  },
  collaboration_leadership: {
    summary: 'Work style, collaboration behavior, ownership, and leadership actions.',
    onetMapping: 'Work styles and interpersonal requirements',
    shrmMapping: 'Behavioral competency and leadership impact',
  },
};

export type RadarDimensionLabelVariant = 'short' | 'full';

export function getRadarDimensionLabel(
  key: RadarDimensionKey,
  variant: RadarDimensionLabelVariant = 'full',
): string {
  const labels = RADAR_DIMENSION_LABELS[key];
  return variant === 'short' ? labels.shortLabel : labels.fullLabel;
}

export function getRadarDimensionTooltipMeta(key: RadarDimensionKey): RadarDimensionTooltipMeta {
  return RADAR_DIMENSION_TOOLTIPS[key];
}

export const DEFAULT_RADAR_DIMENSION_WEIGHTS: Record<RadarDimensionKey, number> = {
  technical_skills: 0.125,
  experience_level: 0.125,
  domain_expertise: 0.125,
  problem_solving: 0.125,
  tech_stack: 0.125,
  transferable_skills: 0.125,
  education_certs: 0.125,
  collaboration_leadership: 0.125,
};
